/* eslint-disable */
import React, { useState, useRef } from 'react';
import { formatSize } from 'src/utils/formatters';
import { trackEvent } from 'src/utils/analytics';

export const ResumeUpload = ({
  register,
  errors,
  fileUploadError,
  handleFileSelection,
  validateFile,
  isUploadReady,
  clearFile,
}) => {
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [dragover, setDragover] = useState(false);

  // Keep a local ref so we can programmatically open the picker from both
  // the upload zone and the chip "remove" flow — without ever unmounting the input.
  const inputRef = useRef(null);

  const registerProps = register('resume', {
    required: 'Resume is required',
    validate: validateFile,
    onChange: (e) => {
      const file = e.target.files[0];
      console.debug('[ResumeUpload] onChange fired — file:', file?.name, '| files:', e.target.files);
      if (file) {
        trackEvent('resume_file_selected', 'Signup', file.type || 'unknown');
      }
      handleFileSelection(e);
      setFileName(file?.name || '');
      setFileSize(file?.size || 0);
    },
  });

  // Merge react-hook-form's ref with our local inputRef so both stay in sync.
  // This is critical: if we only forwarded registerProps.ref, our local ref would
  // be stale after RHF re-registers the field.
  const mergedRef = (el) => {
    inputRef.current = el;
    if (typeof registerProps.ref === 'function') {
      registerProps.ref(el);
    } else if (registerProps.ref) {
      registerProps.ref.current = el;
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragover(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    console.debug('[ResumeUpload] drop — file:', file.name);
    setFileName(file.name);
    setFileSize(file.size);
    const fakeEvent = { target: { files: [file] } };
    handleFileSelection(fakeEvent);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    console.debug('[ResumeUpload] remove clicked — resetting field');
    setFileName('');
    setFileSize(0);
    if (inputRef.current) inputRef.current.value = '';
    if (clearFile) clearFile();
  };

  const openPicker = () => {
    console.debug('[ResumeUpload] openPicker — triggering input click');
    inputRef.current?.click();
  };

  const hasError = errors.resume?.message || fileUploadError;
  const fileSelected = isUploadReady && !!fileName;

  return (
    <div className="tb-field resume-enter" style={{ animation: 'tb-panelIn 0.35s ease-out' }}>
      <label htmlFor="resume-file-input" className="tb-label">
        Resume <span className="tb-req">*</span>
      </label>

      {/*
        ── SINGLE hidden input, always in the DOM ──────────────────────────────
        KEY FIX: previously there were TWO <input type="file"> elements — one
        inside the upload zone and one inside the chip. When the component
        switched branches, the zone input unmounted and the chip input mounted.
        react-hook-form's ref callback fired on the NEW (empty) element, so on
        submit it read `files` from an empty input → "Resume is required".

        Now there is exactly ONE input, always mounted, ref never changes.
      */}
      <input
        type="file"
        accept=".pdf,.doc,.docx"
        id="resume-file-input"
        onChange={registerProps.onChange}
        onBlur={registerProps.onBlur}
        name={registerProps.name}
        ref={mergedRef}
        style={{ display: 'none' }}
      />

      {/* ── File chip shown after selection ── */}
      {fileSelected ? (
        <div className="tb-file-chip">
          {/* Document icon */}
          <div className="tb-file-chip-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
          </div>

          {/* Name + size */}
          <div className="tb-file-chip-info">
            <span className="tb-file-chip-name">{fileName}</span>
            <span className="tb-file-chip-size">{formatSize(fileSize)}</span>
          </div>

          {/* Remove button */}
          <button
            type="button"
            className="tb-file-chip-remove"
            onClick={handleRemove}
            aria-label="Remove file"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      ) : (
        /* ── Upload zone shown before selection ── */
        <div
          className={`tb-upload-zone${dragover ? ' dragover' : ''}${hasError ? ' error' : ''}`}
          onDragEnter={(e) => { e.preventDefault(); setDragover(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragover(true); }}
          onDragLeave={() => setDragover(false)}
          onDrop={handleDrop}
          onClick={openPicker}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openPicker();
            }
          }}
          tabIndex={0}
          role="button"
          aria-label="Upload resume"
        >
          <div className="tb-upload-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>

          <p><strong>Click to upload</strong> or drag &amp; drop</p>
          <div className="tb-file-hint">PDF or DOCX, max 2 MB</div>
        </div>
      )}

      {hasError && (
        <div className="tb-field-error">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{hasError}</span>
        </div>
      )}
    </div>
  );
};
